import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { RootState } from "setup";
import { IDirectorsService } from "services/DirectorsService";
import { NewDirectorModel, ValidationProblemDetails } from "services/api/cplapi/CPLAPI";

const initialState = {
  IsBusy: false,
  IsCreated: false,
};

type CreateDirectorResult =
  | { directorID: string; validationErrors?: undefined; title?: undefined }
  | { validationErrors: Record<string, string[]>; title?: string; directorID?: undefined };

export const createDirector = createAsyncThunk(
  "Directors/CreateDirector",
  async (director: NewDirectorModel): Promise<CreateDirectorResult> => {
    const directorsService = container.get<IDirectorsService>(SERVICE_TYPES.DirectorsService);
    try {
      const directorID = await directorsService.createDirector(director);
      return { directorID };
    } catch (e) {
      if (e instanceof ValidationProblemDetails) {
        return { validationErrors: e.errors ?? {}, title: e.title };
      }
      throw e;
    }
  }
);

export const directorFormSlice = createSlice({
  name: "directorForm",
  initialState,
  reducers: {
    resetState: (state) => {
      state.IsBusy = false;
      state.IsCreated = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createDirector.pending, (state) => {
        state.IsBusy = true;
        state.IsCreated = false;
      })
      .addCase(createDirector.rejected, (state) => {
        state.IsBusy = false;
        toast.error("Director failed to create");
      })
      .addCase(createDirector.fulfilled, (state, action) => {
        state.IsBusy = false;
        if (!action.payload.validationErrors) {
          state.IsCreated = true;
          toast.success("Director created");
        }
      });
  },
});

export const { resetState } = directorFormSlice.actions;

export const selectIsBusy = (state: RootState) => state.director.IsBusy;
export const selectIsCreated = (state: RootState) => state.director.IsCreated;

export default directorFormSlice.reducer;
